// ============================================================================
// ScansionaBolla.jsx v4 — FORMATO IDENTICO A MARCO
//
// Scanner bolla per vista Operaio BertocProject.
// Salva le bolle nello stesso formato del form manuale di Marco, cosi' appaiono
// correttamente nei cantieri e nel consuntivo.
//
// Miglioramenti v4:
// - Righe con campi: materialeDiz, prodotto, qta (stringa), um, prezzo, cantiere,
//   lavorazione, categoriaP, note, totaleRiga (identici a Marco)
// - Scelta sottocategoria ripristinata (per assegnare i materiali alla lavorazione)
// - MATCH AUTOMATICO lavorazione: se categoria+sottocategoria corrispondono a
//   una lavorazione del cantiere, il campo "lavorazione" viene popolato
//   automaticamente, cosi' i materiali finiscono nel consuntivo lavorazione giusto
// - Campo bolla: "num" (non "numBolla")
//
// DIPENDENZE: jspdf
//
// PROPS:
//   cantieriDB         : array cantieri (con lavorazioni)
//   dizionarioMateriali: oggetto {catKey: {label, sottocategorie: {...}}}
//   nomeOperaio        : string
//   onFine             : function() -> torna al menu
// ============================================================================

import React, { useState, useRef, useEffect } from 'react';
import { jsPDF } from 'jspdf';
import { db } from './firebase';
import {collection, addDoc, getDocs} from "./firebase-shim"; // getDocs per FIX_NO_DUP_BOLLE_v1

const theme = {
  bg: "#0f1117",
  surface: "#1a1d27",
  border: "#2a2e45",
  accent: "#f5a623",
  text: "#e8eaf2",
  textMuted: "#7b82a0",
  textDim: "#4a5068",
  success: "#3ecf8e",
  error: "#f06060",
};

const inputSt = {
  background: "#0f1117", border: "1px solid #2a2e45", borderRadius: 10,
  color: "#e8eaf2", padding: "14px 16px", fontSize: 16,
  fontFamily: "'DM Sans',sans-serif", outline: "none", width: "100%",
  boxSizing: "border-box",
};

const btnPrimarioSt = {
  background: theme.accent, color: "#1a1d27", fontWeight: 700, fontSize: 16,
  border: "none", borderRadius: 10, padding: "14px 20px", cursor: "pointer",
  fontFamily: "'DM Sans',sans-serif", width: "100%",
};

const btnSecondarioSt = {
  background: "transparent", color: theme.text, fontWeight: 500, fontSize: 15,
  border: `1px solid ${theme.border}`, borderRadius: 10, padding: "12px 18px",
  cursor: "pointer", fontFamily: "'DM Sans',sans-serif",
};

const cardSt = {
  background: theme.surface, border: `1px solid ${theme.border}`,
  borderRadius: 12, padding: 20, marginBottom: 16,
};

const labelSt = {
  fontSize: 12, color: theme.textDim, textTransform: "uppercase",
  letterSpacing: 1, marginBottom: 8,
};

// ============================================================================

export default function ScansionaBolla({ cantieriDB = [], dizionarioMateriali = {}, nomeOperaio = "", onFine, creatoDaRuolo = "operaio" }) {
  const [cantiere, setCantiere] = useState("");
  // FIX_TELEFONO_LAVORAZIONE_v2: dropdown lavorazione diretto invece di categoria+sottocategoria
  const [lavorazioneScelta, setLavorazioneScelta] = useState("");
  const categoria = "";  // deprecato, mantenuto per retrocompat dei riferimenti residui
  const sottocategoria = "";

  const [fotoOriginale, setFotoOriginale] = useState(null);
  const [modoRitaglio, setModoRitaglio] = useState(false);

  const [analisiInCorso, setAnalisiInCorso] = useState(false);
  const [datiEstratti, setDatiEstratti] = useState(null);
  const [errore, setErrore] = useState(null);

  const [salvando, setSalvando] = useState(false);
  const [successo, setSuccesso] = useState(false);

  const inputFileRef = useRef(null);

  const cantieriAttivi = cantieriDB.filter(c => !c.completato);
  const cantSelezionato = cantieriDB.find(c => c.nome === cantiere);

  // Categorie dal cantiere
  const categorieCantiere = (() => {
    if (!cantSelezionato) return [];
    const set = new Set();
    (cantSelezionato.lavorazioni || []).forEach(l => {
      if (l.categoria) set.add(l.categoria);
    });
    return Array.from(set).map(catKey => ({
      key: catKey,
      label: dizionarioMateriali[catKey]?.label || catKey,
    })).sort((a, b) => a.label.localeCompare(b.label));
  })();

  // Sottocategorie dal cantiere (per categoria scelta)
  const sottocategorieCantiere = (() => {
    if (!cantSelezionato || !categoria) return [];
    const set = new Set();
    (cantSelezionato.lavorazioni || []).forEach(l => {
      if (l.categoria === categoria && l.sottocategoria) set.add(l.sottocategoria);
    });
    const catDizio = dizionarioMateriali[categoria];
    return Array.from(set).map(subKey => ({
      key: subKey,
      label: catDizio?.sottocategorie?.[subKey]?.label || subKey,
    })).sort((a, b) => a.label.localeCompare(b.label));
  })();

  // FIX_TELEFONO_LAVORAZIONE_v2: dropdown lavorazione diretto (vista corta)
  const lavorazioniCantiere = (() => {
    if (!cantSelezionato) return [];
    return (cantSelezionato.lavorazioni || []); /* BOLLA_SMALTIMENTO_v1: anche voci smaltimento (bolle discarica) */
  })();
  const lavorazioneSceltaObj = lavorazioniCantiere.find(l => l.desc === lavorazioneScelta) || null;
  const lavorazioneMatch = lavorazioneSceltaObj?.desc || "";
  const lavorazioneMatchDisplay = (lavorazioneSceltaObj?.descBreve||'').trim() || lavorazioneSceltaObj?.desc || "";

  const resetTutto = () => {
    setCantiere("");
    setLavorazioneScelta("");
    setFotoOriginale(null);
    setModoRitaglio(false);
    setDatiEstratti(null);
    setErrore(null);
    setSuccesso(false);
  };

  const handleFotoScattata = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      setFotoOriginale(ev.target.result);
      setDatiEstratti(null);
      setModoRitaglio(true);
      setErrore(null);
      setSuccesso(false);
    };
    reader.readAsDataURL(file);
    if (inputFileRef.current) inputFileRef.current.value = "";
  };

  const handleRitaglioConfermato = async (dataURLRitagliata) => {
    setModoRitaglio(false);
    setAnalisiInCorso(true);
    setErrore(null);
    try {
      const pdfDataUrl = await immagineToPDF(dataURLRitagliata);
      const base64 = pdfDataUrl.split(',')[1];
      const resp = await fetch('/api/analizza-bolla', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pdfBase64: base64 }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${resp.status}`);
      }
      const dati = await resp.json();
      // Estrai TUTTI i campi del nuovo schema v2 (con fallback ai vecchi per retrocompat)
      setDatiEstratti({
        numBolla: dati.numBolla || "",
        fornitore: dati.fornitore || "",
        data: dati.data || new Date().toISOString().slice(0, 10),
        cantiereId: dati.cantiereId || "",
        cantiereNome: dati.cantiereNome || dati.cantiere || "",
        destinazione: dati.destinazione || (dati.cantiereId ? "cantiere" : "magazzino"),
        righe: (dati.righe || []).map(r => {
          const totale = r.qtaTotale !== undefined && r.qtaTotale !== null ? r.qtaTotale : r.qta;
          const umBase = r.umBase || r.um || "cad";
          return {
            descrizione: r.descrizione || "",
            qta: totale != null ? totale : 0,
            um: umBase,
            note: r.note || "",
            // Nuovi campi v2 (salvati su Firestore per coerenza con Marco, non visibili in UI operaio)
            qtaUnitaria: r.qtaUnitaria != null ? r.qtaUnitaria : null,
            numeroConfezioni: r.numeroConfezioni != null ? r.numeroConfezioni : null,
            qtaTotale: r.qtaTotale != null ? r.qtaTotale : null,
            umBase: r.umBase || "",
            umConfezione: r.umConfezione || "",
            codiceArticolo: r.codiceArticolo || "",
            materialeId: r.materialeId || null,
            isNuovo: r.isNuovo !== undefined ? r.isNuovo : true,
            famigliaSuggerita: r.famigliaSuggerita || "",
          };
        }),
      });
    } catch (err) {
      console.error(err);
      setErrore("AI non ha letto la bolla: " + err.message);
    } finally {
      setAnalisiInCorso(false);
    }
  };

  // SALVATAGGIO - formato IDENTICO a Marco
  const handleSalva = async () => {
    if (!cantiere) { alert("Scegli il cantiere o il magazzino"); return; }
    if (!datiEstratti) { alert("Scannerizza prima la bolla"); return; }
    setSalvando(true);
    setErrore(null);
    try {
      const stato = lavorazioneSceltaObj ? 'completa' : 'da_completare';

      // Righe formato Marco: prodotto (non descrizione), cantiere dentro riga,
      // qta stringa, prezzo stringa vuota, categoriaP vuota (e' famiglia prodotto, non categoria lavorazione),
      // lavorazione dal match automatico, totaleRiga 0
      // Schema riga IDENTICO a Marco (gestionale): include tutti i campi v2
      const righePulite = (datiEstratti.righe || []).map(r => ({
        materialeDiz: "",
        prodotto: r.descrizione || "",
        qta: String(r.qta != null ? r.qta : ""),
        um: r.um || "cad",
        // Nuovi campi confezione (per UI Marco e magazzino)
        nConf: r.numeroConfezioni != null ? String(r.numeroConfezioni) : "",
        contConf: r.qtaUnitaria != null ? String(r.qtaUnitaria) : "",
        umConf: r.umConfezione || "",
        // Nuovi campi metadata
        codice: r.codiceArticolo || "",
        materialeId: r.materialeId || null,
        isNuovo: r.isNuovo !== undefined ? r.isNuovo : true,
        prezzo: "",
        cantiere: cantiere === "__MAGAZZINO__" ? "" : cantiere,
        cantiereId: cantiere === "__MAGAZZINO__" ? "" : (datiEstratti.cantiereId || ""),
        lavorazione: cantiere === "__MAGAZZINO__" ? "" : (lavorazioneMatch || ""),
        categoriaP: r.famigliaSuggerita || "",
        note: r.note || "",
        totaleRiga: 0,
      }));

      const payload = {
        num: datiEstratti.numBolla || "",
        fornitore: datiEstratti.fornitore || "",
        data: datiEstratti.data || new Date().toISOString().slice(0, 10),
        note: "",
        righe: righePulite,
        totale: 0,
        createdAt: new Date().toISOString(),
        // Nuovi campi v2 (cantiere AI + destinazione)
        cantiereId: cantiere === "__MAGAZZINO__" ? "" : (datiEstratti.cantiereId || ""),
        cantiereNomeAI: cantiere === "__MAGAZZINO__" ? "" : (datiEstratti.cantiereNome || ""),
        destinazione: cantiere === "__MAGAZZINO__" ? "magazzino" : (datiEstratti.destinazione || (datiEstratti.cantiereId ? "cantiere" : "magazzino")),
        // campi extra per flusso operaio
        stato,
        creatoDa: creatoDaRuolo,
        operaioNome: nomeOperaio || "",
      };
      // FIX_NO_DUP_BOLLE_v1: blocco se esiste gia' una bolla con stesso num+fornitore+data
      try {
        const snapEx = await getDocs(collection(db, 'bolle'));
        const numN = (payload.num||"").trim().toLowerCase();
        const fornN = (payload.fornitore||"").trim().toLowerCase();
        const dataN = (payload.data||"").trim();
        const dup = snapEx.docs.map(d => d.data()).find(b =>
          (b.num||"").trim().toLowerCase() === numN &&
          (b.fornitore||"").trim().toLowerCase() === fornN &&
          (b.data||"").trim() === dataN
        );
        if (dup) {
          setErrore("Bolla gia' presente: N. " + payload.num + " del " + payload.data + " - fornitore " + (payload.fornitore||"(vuoto)") + ". Verifica nell'elenco bolle.");
          return;
        }
      } catch (errDup) {
        console.warn('FIX_NO_DUP_BOLLE_v1 verifica duplicati fallita, procedo:', errDup.message);
      }

      // FIX_SCANSIONA_BOLLA_PREZZI_V3: match cascading (codice/nome/starts-with) + scelta €/conf vs €/kg
      try {
        const snapList = await getDocs(collection(db, 'listino_materiali'));
        const listino = snapList.docs.map(d => ({...d.data(), _id: d.id}));
        const normNome = (s) => (s||"").toUpperCase().replace(/[.,\-]/g, ' ').replace(/\s+/g, ' ').trim();
        const normCod = (s) => (s||"").toUpperCase().replace(/[\s\-.]/g, '').trim();
        const cerca = (riga) => {
          const cod = normCod(riga.codice);
          const nomeN = normNome(riga.prodotto);
          // 1) match per codice fornitore
          if (cod) {
            const byCod = listino.find(l => {
              const codes = Array.isArray(l.codiciFornitore) ? l.codiciFornitore : [];
              return codes.some(c => normCod(c) === cod);
            });
            if (byCod) return byCod;
          }
          // 2) match per nome normalizzato esatto
          if (nomeN) {
            const byNome = listino.find(l => normNome(l.prodotto) === nomeN);
            if (byNome) return byNome;
          }
          // 3) match starts-with (per nomi troncati/con suffissi)
          if (nomeN && nomeN.length >= 10) {
            const candidati = listino.filter(l => {
              const ln = normNome(l.prodotto);
              if (!ln || ln.length < 10) return false;
              return ln.startsWith(nomeN) || nomeN.startsWith(ln);
            });
            if (candidati.length > 0) {
              candidati.sort((a, b) => (a.prodotto||"").length - (b.prodotto||"").length);
              return candidati[0];
            }
          }
          return null;
        };
        payload.righe = (payload.righe||[]).map(r => {
          const prezzoNum = parseFloat(r.prezzo);
          const haPrezzo = !isNaN(prezzoNum) && prezzoNum > 0;
          if (haPrezzo) return r;
          if (!r.prodotto) return r;
          const trovato = cerca(r);
          if (!trovato || !trovato.prezzoUltimo) return r;
          // Scelta prezzo: umBolla === umBase listino → €/UM base, altrimenti €/conf
          const umBolla = (r.um||'').toLowerCase().trim();
          const umBase = (trovato.umBase||'').toLowerCase().trim();
          let prezzoApplicato, umFinale;
          if (umBolla && umBase && umBolla === umBase && trovato.prezzoUltimoBase > 0) {
            prezzoApplicato = trovato.prezzoUltimoBase;
            umFinale = umBolla;
          } else {
            prezzoApplicato = trovato.prezzoUltimo;
            umFinale = r.um || trovato.um || 'cad';
          }
          return {
            ...r,
            prezzo: prezzoApplicato,
            um: umFinale,
            categoriaP: r.categoriaP || trovato.categoriaP || '',
            totaleRiga: (parseFloat(r.qta)||0) * prezzoApplicato,
          };
        });
        payload.totale = (payload.righe||[]).reduce((s,r) => s + (parseFloat(r.totaleRiga)||0), 0);
      } catch (errListino) {
        console.warn('FIX_SCANSIONA_BOLLA_PREZZI_V3 lookup listino fallito, procedo senza prezzi:', errListino.message);
      }

      await addDoc(collection(db, 'bolle'), payload);

      // ROLLBACK_FIX_LISTINO_BOLLE_v1: la scansione bolla NON popola piu' il listino.
      // Il listino e' alimentato SOLO dalle fatture. Le bolle leggono prezzi dal listino.

      setSuccesso(true);
      setFotoOriginale(null);
      setDatiEstratti(null);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.error(err);
      setErrore("Errore salvataggio: " + err.message);
    } finally {
      setSalvando(false);
    }
  };

  if (modoRitaglio && fotoOriginale) {
    return (
      <Ritaglio
        immagine={fotoOriginale}
        onAnnulla={() => { setModoRitaglio(false); setFotoOriginale(null); }}
        onConferma={handleRitaglioConfermato}
      />
    );
  }

  return (
    <div className="content" style={{ maxWidth: 520, margin: "0 auto" }}>

      <div style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: 22, color: theme.accent, marginBottom: 8 }}>
        📷 Scansiona Bolla
      </div>
      <div style={{ fontSize: 14, color: theme.textMuted, marginBottom: 24 }}>
        Fotografa la bolla del fornitore e l'AI estrae i dati
      </div>

      {successo && (
        <div style={{ background: "#0a2218", border: `1px solid ${theme.success}44`, borderRadius: 12, padding: "14px 18px", marginBottom: 20, display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontSize: 24 }}>✅</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, color: theme.success, fontSize: 16 }}>Bolla salvata!</div>
            <div style={{ fontSize: 12, color: theme.textMuted, marginTop: 2 }}>
              {(categoria && sottocategoria) ? "Materiali attribuiti alla lavorazione." : "L'ufficio completerà categoria e prezzi."}
            </div>
          </div>
          <button onClick={resetTutto} style={{ ...btnPrimarioSt, width: "auto", padding: "10px 14px", fontSize: 14 }}>
            📷 Nuova
          </button>
        </div>
      )}

      {errore && (
        <div style={{ background: "#2a0f12", border: `1px solid ${theme.error}44`, borderRadius: 12, padding: "14px 18px", marginBottom: 20 }}>
          <div style={{ fontWeight: 700, color: theme.error, fontSize: 14, marginBottom: 4 }}>⚠️ Errore</div>
          <div style={{ fontSize: 13, color: theme.text }}>{errore}</div>
        </div>
      )}

      {/* Card: Cantiere + Categoria + Sottocategoria */}
      <div style={cardSt}>

        <div style={{ marginBottom: 16 }}>
          <div style={labelSt}>Cantiere *</div>
          <select
            style={inputSt}
            value={cantiere}
            onChange={e => { setCantiere(e.target.value); setLavorazioneScelta(""); }}>
            <option value="">Seleziona...</option>
            <option value="__MAGAZZINO__">📦 Magazzino</option>
            {cantieriAttivi.sort((a, b) => a.nome.localeCompare(b.nome)).map(c => (
              <option key={c._id || c.id} value={c.nome}>{c.nome}</option>
            ))}
          </select>
        </div>

        {/* FIX_TELEFONO_LAVORAZIONE_v2: dropdown lavorazione diretta (vista corta) */}
        {cantiere && cantiere !== "__MAGAZZINO__" && (
          <div>
            <div style={labelSt}>
              Lavorazione
              <span style={{ color: theme.textDim, textTransform: "none", letterSpacing: 0, marginLeft: 6, fontSize: 11 }}>
                (opzionale)
              </span>
            </div>
            {lavorazioniCantiere.length === 0 ? (
              <div style={{ fontSize: 13, color: theme.textDim, fontStyle: "italic", padding: "10px 4px" }}>
                Nessuna lavorazione in questo cantiere
              </div>
            ) : (
              <>
                <select
                  style={inputSt}
                  value={lavorazioneScelta}
                  onChange={e => setLavorazioneScelta(e.target.value)}>
                  <option value="">— Non specificata —</option>
                  {lavorazioniCantiere.map((l,i) => {
                    const breve = (l.descBreve||'').trim();
                    const lunga = (l.desc||'').trim();
                    /* NUM_VOCE_SCANNER_v1 */
                    const nv = (l.numVoce||'').trim();
                    return <option key={i} value={l.desc}>{(nv ? nv+' - ' : '') + (breve || lunga)}</option>;
                  })}
                </select>
                {lavorazioneSceltaObj && (
                  <div style={{ marginTop: 8, fontSize: 12, color: theme.success }}>
                    ✓ Materiali attribuiti a: <strong>{lavorazioneMatchDisplay}</strong>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Card: Foto */}
      {cantiere && !datiEstratti && (
        <div style={cardSt}>
          <div style={{ ...labelSt, marginBottom: 12 }}>Foto bolla</div>
          <input
            ref={inputFileRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleFotoScattata}
            style={{ display: 'none' }}
          />
          {!analisiInCorso ? (
            <button style={btnPrimarioSt} onClick={() => inputFileRef.current?.click()}>
              📷 Apri fotocamera
            </button>
          ) : (
            <div style={{ textAlign: "center", padding: "20px 0" }}>
              <div style={spinnerSt} />
              <div style={{ marginTop: 14, color: theme.textMuted, fontSize: 14 }}>
                Lettura in corso...
              </div>
            </div>
          )}
        </div>
      )}

      {/* Card: Dati estratti */}
      {datiEstratti && (
        <div style={cardSt}>
          <div style={{ ...labelSt, marginBottom: 14 }}>Dati letti dalla bolla</div>

          <Campo label="Fornitore" value={datiEstratti.fornitore} onChange={v => setDatiEstratti({ ...datiEstratti, fornitore: v })} />
          <Campo label="Numero bolla" value={datiEstratti.numBolla} onChange={v => setDatiEstratti({ ...datiEstratti, numBolla: v })} />
          <Campo label="Data" value={datiEstratti.data} onChange={v => setDatiEstratti({ ...datiEstratti, data: v })} type="date" />

          <div style={{ marginTop: 18, marginBottom: 10, fontSize: 13, color: theme.textMuted, fontWeight: 600 }}>
            Materiali ({datiEstratti.righe.length})
          </div>

          {datiEstratti.righe.length === 0 && (
            <div style={{ fontSize: 13, color: theme.textDim, fontStyle: "italic", marginBottom: 10 }}>
              Nessun materiale rilevato. Aggiungine manualmente.
            </div>
          )}

          {datiEstratti.righe.map((r, idx) => (
            <div key={idx} style={{ background: theme.bg, border: `1px solid ${theme.border}44`, borderRadius: 10, padding: 12, marginBottom: 10 }}>
              <input
                style={{ ...inputSt, marginBottom: 8, fontSize: 14 }}
                placeholder="Descrizione materiale"
                value={r.descrizione}
                onChange={e => {
                  const nuove = [...datiEstratti.righe];
                  nuove[idx] = { ...nuove[idx], descrizione: e.target.value };
                  setDatiEstratti({ ...datiEstratti, righe: nuove });
                }}
              />
              <div style={{ display: "flex", gap: 8 }}>
                <input
                  style={{ ...inputSt, flex: 1, fontSize: 14 }}
                  type="number" step="0.01" placeholder="Qta"
                  value={r.qta}
                  onChange={e => {
                    const nuove = [...datiEstratti.righe];
                    nuove[idx] = { ...nuove[idx], qta: parseFloat(e.target.value) || 0 };
                    setDatiEstratti({ ...datiEstratti, righe: nuove });
                  }}
                />
                <select
                  style={{ ...inputSt, flex: 1, fontSize: 14 }}
                  value={r.um}
                  onChange={e => {
                    const nuove = [...datiEstratti.righe];
                    nuove[idx] = { ...nuove[idx], um: e.target.value };
                    setDatiEstratti({ ...datiEstratti, righe: nuove });
                  }}>
                  <option value="kg">kg</option>
                  <option value="lt">lt</option>
                  <option value="ml">ml</option>
                  <option value="mq">mq</option>
                  <option value="mc">mc</option>
                  <option value="cad">cad</option>
                </select>
                <button
                  onClick={() => setDatiEstratti({ ...datiEstratti, righe: datiEstratti.righe.filter((_, i) => i !== idx) })}
                  style={{ ...btnSecondarioSt, color: theme.error, padding: "0 14px", borderColor: `${theme.error}44` }}>
                  ✕
                </button>
              </div>
              {r.note && (
                <div style={{ fontSize: 11, color: theme.textDim, marginTop: 6, fontStyle: "italic" }}>
                  {r.note}
                </div>
              )}
            </div>
          ))}

          <button
            onClick={() => setDatiEstratti({
              ...datiEstratti,
              righe: [...datiEstratti.righe, { descrizione: "", qta: 1, um: "cad", note: "" }]
            })}
            style={{ ...btnSecondarioSt, width: "100%", marginBottom: 14 }}>
            + Aggiungi riga
          </button>

          <div style={{ display: "flex", gap: 10 }}>
            <button
              onClick={() => { setDatiEstratti(null); setFotoOriginale(null); }}
              disabled={salvando}
              style={{ ...btnSecondarioSt, flex: 1 }}>
              Rifai foto
            </button>
            <button
              onClick={handleSalva}
              disabled={salvando}
              style={{ ...btnPrimarioSt, flex: 2, opacity: salvando ? 0.6 : 1 }}>
              {salvando ? "Salvataggio..." : "✓ Salva bolla"}
            </button>
          </div>
        </div>
      )}

      {!datiEstratti && !analisiInCorso && onFine && (
        <button onClick={onFine} style={{ ...btnSecondarioSt, width: "100%", marginTop: 10 }}>
          ← Torna al menu
        </button>
      )}

    </div>
  );
}

function Campo({ label, value, onChange, type = "text" }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={labelSt}>{label}</div>
      <input
        type={type}
        value={value || ""}
        onChange={e => onChange(e.target.value)}
        style={inputSt}
      />
    </div>
  );
}

function Ritaglio({ immagine, onAnnulla, onConferma }) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [angoli, setAngoli] = useState(null);
  const [trascinaIdx, setTrascinaIdx] = useState(null);
  const [imgSize, setImgSize] = useState({ w: 0, h: 0 });
  const [canvasSize, setCanvasSize] = useState({ w: 0, h: 0 });

  useEffect(() => {
    if (!immagine) return;
    const img = new Image();
    img.onload = () => {
      const containerW = Math.min(containerRef.current?.clientWidth || 360, 480);
      const ratio = img.height / img.width;
      const cw = containerW;
      const ch = containerW * ratio;
      setImgSize({ w: img.width, h: img.height });
      setCanvasSize({ w: cw, h: ch });
      const margin = 20;
      setAngoli([
        { x: margin, y: margin },
        { x: cw - margin, y: margin },
        { x: cw - margin, y: ch - margin },
        { x: margin, y: ch - margin },
      ]);
    };
    img.src = immagine;
  }, [immagine]);

  useEffect(() => {
    const cv = canvasRef.current;
    if (!cv || !angoli || !immagine) return;
    cv.width = canvasSize.w;
    cv.height = canvasSize.h;
    const ctx = cv.getContext('2d');
    const img = new Image();
    img.onload = () => {
      ctx.drawImage(img, 0, 0, canvasSize.w, canvasSize.h);
      ctx.fillStyle = 'rgba(0,0,0,0.6)';
      ctx.fillRect(0, 0, canvasSize.w, canvasSize.h);
      ctx.globalCompositeOperation = 'destination-out';
      ctx.beginPath();
      ctx.moveTo(angoli[0].x, angoli[0].y);
      for (let i = 1; i < 4; i++) ctx.lineTo(angoli[i].x, angoli[i].y);
      ctx.closePath();
      ctx.fill();
      ctx.globalCompositeOperation = 'source-over';
      ctx.strokeStyle = theme.accent;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(angoli[0].x, angoli[0].y);
      for (let i = 1; i < 4; i++) ctx.lineTo(angoli[i].x, angoli[i].y);
      ctx.closePath();
      ctx.stroke();
      angoli.forEach(a => {
        ctx.fillStyle = theme.surface;
        ctx.strokeStyle = theme.accent;
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.arc(a.x, a.y, 14, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
      });
    };
    img.src = immagine;
  }, [angoli, canvasSize, immagine]);

  const getEventPos = e => {
    const rect = canvasRef.current.getBoundingClientRect();
    const t = e.touches?.[0] || e.changedTouches?.[0];
    const cx = t ? t.clientX : e.clientX;
    const cy = t ? t.clientY : e.clientY;
    return { x: cx - rect.left, y: cy - rect.top };
  };
  const handleStart = e => {
    e.preventDefault();
    const pos = getEventPos(e);
    let bestIdx = -1, bestDist = 35;
    angoli.forEach((a, i) => {
      const d = Math.hypot(a.x - pos.x, a.y - pos.y);
      if (d < bestDist) { bestDist = d; bestIdx = i; }
    });
    if (bestIdx >= 0) setTrascinaIdx(bestIdx);
  };
  const handleMove = e => {
    if (trascinaIdx === null) return;
    e.preventDefault();
    const pos = getEventPos(e);
    const x = Math.max(0, Math.min(canvasSize.w, pos.x));
    const y = Math.max(0, Math.min(canvasSize.h, pos.y));
    setAngoli(angoli.map((a, i) => i === trascinaIdx ? { x, y } : a));
  };
  const handleEnd = () => setTrascinaIdx(null);

  const conferma = () => {
    if (!angoli) return;
    const sx = imgSize.w / canvasSize.w;
    const sy = imgSize.h / canvasSize.h;
    const reali = angoli.map(a => ({ x: a.x * sx, y: a.y * sy }));
    const minX = Math.min(...reali.map(a => a.x));
    const maxX = Math.max(...reali.map(a => a.x));
    const minY = Math.min(...reali.map(a => a.y));
    const maxY = Math.max(...reali.map(a => a.y));
    const w = maxX - minX, h = maxY - minY;
    const img = new Image();
    img.onload = () => {
      const out = document.createElement('canvas');
      out.width = w;
      out.height = h;
      const ctx = out.getContext('2d');
      ctx.drawImage(img, minX, minY, w, h, 0, 0, w, h);
      onConferma(out.toDataURL('image/jpeg', 0.85));
    };
    img.src = immagine;
  };

  return (
    <div className="content" style={{ maxWidth: 520, margin: "0 auto" }}>
      <div style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: 22, color: theme.accent, marginBottom: 8 }}>
        ✂️ Ritaglia bolla
      </div>
      <div style={{ fontSize: 14, color: theme.textMuted, marginBottom: 20 }}>
        Trascina i 4 angoli per selezionare il foglio
      </div>
      <div ref={containerRef} style={cardSt}>
        <canvas
          ref={canvasRef}
          style={{ touchAction: "none", maxWidth: "100%", borderRadius: 8, display: "block", margin: "0 auto" }}
          onMouseDown={handleStart}
          onMouseMove={handleMove}
          onMouseUp={handleEnd}
          onMouseLeave={handleEnd}
          onTouchStart={handleStart}
          onTouchMove={handleMove}
          onTouchEnd={handleEnd}
        />
      </div>
      <div style={{ display: "flex", gap: 10 }}>
        <button onClick={onAnnulla} style={{ ...btnSecondarioSt, flex: 1 }}>Rifai</button>
        <button onClick={conferma} style={{ ...btnPrimarioSt, flex: 2, width: "auto" }}>Conferma</button>
      </div>
    </div>
  );
}

async function immagineToPDF(dataURL) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      try {
        const pdf = new jsPDF({ unit: 'mm', format: 'a4' });
        const pageW = 210, pageH = 297;
        const ratio = img.height / img.width;
        let w = pageW - 20, h = w * ratio;
        if (h > pageH - 20) { h = pageH - 20; w = h / ratio; }
        pdf.addImage(dataURL, 'JPEG', (pageW - w) / 2, (pageH - h) / 2, w, h);
        resolve(pdf.output('datauristring'));
      } catch (e) { reject(e); }
    };
    img.onerror = () => reject(new Error('Caricamento immagine fallito'));
    img.src = dataURL;
  });
}

const spinnerSt = {
  width: 44, height: 44, margin: "0 auto",
  border: `4px solid ${theme.border}`, borderTop: `4px solid ${theme.accent}`,
  borderRadius: "50%", animation: "spin 1s linear infinite",
};

if (typeof document !== 'undefined' && !document.getElementById('scansiona-bolla-spin')) {
  const s = document.createElement('style');
  s.id = 'scansiona-bolla-spin';
  s.textContent = '@keyframes spin{to{transform:rotate(360deg)}}';
  document.head.appendChild(s);
}
